import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AssessmentRCSAActivityByAssessmentIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetAssessmentRcsaActivitiesByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC assessmentActivity data to match the GraphQL query structure
 */
const mapTrpcAssessmentActivitiesToGraphQL = (
  trpcData: AssessmentRCSAActivityByAssessmentIdResponseRow[]
): GetAssessmentRcsaActivitiesByParentIdQuery => {
  return {
    assessment_activity: trpcData,
  };
};

export const useGetAssessmentRCSAActivitiesByAssessmentId = (
  assessmentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.assessment.assessmentRCSAActivitiesByAssessmentId.queryOptions(
      { assessmentId }
    ),
    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcAssessmentActivitiesToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
